import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import isScrollbarPress from "components/common/isScrollbarPress";


/**
 * Positioning + dismissal for a popover that is PORTALLED to document.body.
 *
 * Extracted from StatusSelect, where every rule below was learned the hard way:
 *
 * - Portal, don't nest. .appointments-table is `overflow-x: auto`, and CSS
 *   promotes the other axis to `auto` with it, so an absolutely-positioned menu
 *   inside it is clipped on BOTH axes and becomes unclickable. Callers must
 *   ReactDOM.createPortal the menu to document.body and spread `menuStyle`.
 * - Flip above the trigger when there is no room below, else rows near the
 *   bottom open off-screen. A flipped menu is capped so its bottom edge lands
 *   just above the trigger: it is anchored by its top, so any height it gains
 *   after being measured would otherwise land ON the field.
 * - Clamp `left` into the viewport: these menus are wider than their triggers
 *   and the right-most column would push them off the edge.
 * - Cap height and let the menu scroll internally when it still doesn't fit.
 * - Exempt the menu's OWN scroll from the scroll-to-close listener. `scroll`
 *   doesn't bubble so the listener must use capture, which then also catches
 *   the menu scrolling itself — closing it under the user's cursor as they
 *   reach for an option near the bottom.
 *
 * A portalled node leaves its page root, so it loses any scoped `box-sizing`
 * and `font-family`. The menu's own stylesheet must restate both.
 */
const MENU_MARGIN = 6;
const VIEWPORT_PAD = 8;

/**
 * @param open       whether the popover is showing
 * @param onDismiss  called when it should close
 * @param reflowKey  OPTIONAL. Any value that changes when the menu's own
 *   CONTENT changes size — an option count, typically. The position is measured
 *   from the menu's height, and a flipped menu is anchored by its TOP
 *   (`triggerTop - menuHeight`), so a menu that shrinks after being placed keeps
 *   its old top and its bottom edge lifts away from the trigger: filter a
 *   fifteen-row list down to one and the remaining row floats hundreds of pixels
 *   above the field it belongs to. Pass this and the position is recomputed.
 *   Callers with fixed content can leave it out — undefined never changes, so
 *   their behaviour is exactly what it was.
 */
export const usePopover = (open, onDismiss, reflowKey) => {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    if (!triggerRef.current) return;
    const t = triggerRef.current.getBoundingClientRect();
    const menuH = (menuRef.current && menuRef.current.offsetHeight) || 0;
    const menuW = (menuRef.current && menuRef.current.offsetWidth) || 0;
    const below = window.innerHeight - t.bottom - MENU_MARGIN;
    /* The VISIBLE width. window.innerWidth counts a classic scrollbar, so on
       Windows (~17px) a menu kept on screen against it ended UNDER the
       scrollbar with its right edge cut — "Update booking for" lost the end of
       every name (Nittin, 2026-10-07). clientWidth leaves the scrollbar out; on
       macOS the scrollbar overlays the page and the two are equal. */
    const viewW = document.documentElement.clientWidth || window.innerWidth;

    const flip = menuH > 0 && below < menuH && t.top - MENU_MARGIN > below;
    const top = flip
      ? Math.max(VIEWPORT_PAD, t.top - MENU_MARGIN - menuH)
      : t.bottom + MENU_MARGIN;

    setPos({
      top,
      left: Math.max(
        VIEWPORT_PAD,
        Math.min(t.left, viewW - menuW - VIEWPORT_PAD)
      ),
      width: t.width,
      /* MEASURED FROM `top`, never from the top of the viewport. A flipped
         menu is anchored by its TOP, so capping it at the whole space above
         the trigger let it render TALLER than the height its own top was
         computed from — and the surplus painted straight over the field. That
         is how the Rx specialty dropdown came to sit on top of its own input
         (Nittin, 2026-09-12): `.rx-spec-menu` caps itself at 280px in CSS, the
         pre-measure paint therefore reports 280, `top` was placed 280px above
         the field, and then this line handed the menu the entire 486px above
         it to grow into. The bottom edge is now exactly `triggerTop -
         MENU_MARGIN` whatever the content does. Unchanged wherever `top`
         clamps to VIEWPORT_PAD, which is the case every existing caller was
         measured in. */
      maxHeight: flip
        ? t.top - MENU_MARGIN - top
        : window.innerHeight - top - VIEWPORT_PAD,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) {
      place();
      return;
    }
    // Drop the old position on close. Keeping it means the next open's
    // measuring render still has the PREVIOUS maxHeight applied, which caps
    // offsetHeight and makes the flip/clamp maths wrong for the new anchor.
    setPos(null);
    /* `reflowKey` re-runs the measurement when the CONTENT changed size rather
       than when the trigger moved — see the note on the parameter. `place` is a
       useCallback with no deps, so it is stable and this cannot loop: setPos
       renders again, but none of these three has changed by then. */
  }, [open, place, reflowKey]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocClick = (e) => {
      // The menu is portalled, so it is NOT inside triggerRef — both must be
      // checked or clicking an option closes before it registers.
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      /* A grab of the scrollbar is not a click away — see isScrollbarPress. */
      if (!inTrigger && !inMenu && !isScrollbarPress(e)) onDismiss();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onDismiss();
    };
    /* rAF-throttled, so a burst of scroll events re-measures once a frame. */
    let frame = 0;
    const follow = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        place();
      });
    };

    const onScroll = (e) => {
      // Ignore the menu scrolling itself; see the note above.
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) {
        return;
      }
      /* FOLLOW, DON'T CLOSE — everywhere (Nittin, 2026-09-24).

         Scrolling is not the user leaving. It used to close on desktop, on the
         reasoning that a `position: fixed` menu cannot follow a scrolling
         ancestor and would be stranded next to the wrong row — but re-measuring
         IS how it follows, which is what the phone branch had been doing all
         along for the on-screen keyboard. Closing meant a reviewer who nudged
         the page to see the option list lost it, and a drag of the scrollbar
         killed the menu on release.

         A click outside and Escape still close it. Those are the user saying
         so; a scroll is not. */
      follow();
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, onDismiss, place]);

  /* The menu's own WIDTH can change after it was placed, with nothing for
     reflowKey to see: a web font landing, mostly. "Update booking for" sizes
     itself to its longest name, and opened before Montserrat had loaded it
     measured 9px narrower than it then became — the left clamp kept the old
     width and the right edge ran off the screen (Nittin, 2026-10-08). Watch
     the width while open and re-place on a change, a frame later so the
     observer never reports its own adjustment. Width only: a height change is
     what reflowKey is for. */
  useEffect(() => {
    const el = menuRef.current;
    const RO = window.ResizeObserver;
    if (!open || !el || !RO) return undefined;
    let width = el.offsetWidth;
    let frame = 0;
    const ro = new RO(() => {
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      if (!frame) {
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          place();
        });
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [open, place]);

  const menuStyle = pos
    ? { top: pos.top, left: pos.left, minWidth: pos.width, maxHeight: pos.maxHeight }
    : // Pre-measurement paint: keep it off-screen so it can't flash in the
      // wrong place. useLayoutEffect measures before the browser paints.
      { top: -9999, left: -9999 };

  return { triggerRef, menuRef, menuStyle };
};

export default usePopover;
